/**
 * ACTIVITY-CORE (F4-A) — activity feed, SINGLE path via REST.
 *
 * `GET /api/activity` (master log `activity`, keyset-paginated) is the only
 * source: the first page and "load older" come from the same place with the
 * same format. The revert stays server-side
 * (`POST /api/activity/:id/revert`, compensating write).
 *
 * Until 2026-07-27 this screen mixed TWO paths to the same data: the local
 * SQLite's `activity_recent` mirror (PowerSync) for recent items and the API for
 * older ones, with a hand-written camelCase→snake_case adapter between them. Plan 06,
 * E5: the feed is a long tail that means nothing with old data, so the
 * data policy sends it to REST (`docs/DATA_ACCESS_POLICY.md` §2).
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { History, RotateCcw, WifiOff } from "@fleet/icons";
import { type ActivityRow, groupActivityByBatch, isOfflineEdit, queryKeys } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { api, createSafeQuery } from "@/lib/api";
import { showToast } from "@/components/ui/toast";
import { PageHeader, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { Link } from "@tanstack/solid-router";
import { actionText, entityLabel, entityLink, entityTypeText } from "../lib/activity-text";

/** Keyset page size (matches the server's default). */
const PAGE_SIZE = 50;

export const ActivityFeed: Component = () => {
  const { t, locale } = useI18n();
  const { userId } = useSession();

  // The feed's first page. Following ones get APPENDED from the SAME API.
  const feed = createSafeQuery(() => ({
    queryKey: queryKeys.activity.feed(userId()),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/activity", {
        params: { query: { limit: PAGE_SIZE } },
      });
      if (error) {
        throw new Error("activity feed failed");
      }
      return data;
    },
    staleTime: 30_000,
  }));

  const [older, setOlder] = createSignal<ActivityRow[]>([]);
  const [nextBefore, setNextBefore] = createSignal<string | null | undefined>(undefined);
  const [loadingOlder, setLoadingOlder] = createSignal(false);

  const rows = createMemo<ActivityRow[]>(() => [...(feed.data?.items ?? []), ...older()]);

  /** Next page's cursor: the last loaded page's. */
  const cursor = () => (older().length > 0 ? nextBefore() : feed.data?.nextBefore);

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const { data, error } = await api.GET("/api/activity", {
        params: { query: { before: cursor() ?? undefined, limit: PAGE_SIZE } },
      });
      if (error) {
        showToast(t(msg`Could not load older activity`), "error");
        return;
      }
      const seen = new Set(rows().map((r) => r.id));
      setOlder([...older(), ...data.items.filter((it) => !seen.has(it.id))]);
      setNextBefore(data.nextBefore ?? null);
    } finally {
      setLoadingOlder(false);
    }
  };

  const revert = async (row: ActivityRow) => {
    const { error } = await api.POST("/api/activity/{id}/revert", {
      params: { path: { id: row.id } },
    });
    if (error) {
      showToast(t(msg`This action cannot be reverted`), "error");
      return;
    }
    showToast(t(msg`Action reverted`), "success");
    setOlder([]);
    setNextBefore(undefined);
    await feed.refetch();
  };

  const groups = createMemo(() => groupActivityByBatch(rows()));

  const fmtTs = (ts: string | null | undefined) => {
    if (!ts) {
      return "";
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime())
      ? ""
      : new Intl.DateTimeFormat(locale(), { dateStyle: "medium", timeStyle: "short" }).format(d);
  };

  return (
    <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
      <PageHeader title={t(msg`Activity`)} icon={History} />

      <Show
        when={!feed.isPending}
        fallback={
          <div class="space-y-3" data-testid="activity-feed-skeleton">
            <Skeleton class="h-20 w-full rounded-2xl" />
            <Skeleton class="h-20 w-full rounded-2xl" />
            <Skeleton class="h-20 w-full rounded-2xl" />
          </div>
        }
      >
        <Show
          when={groups().length > 0}
          fallback={
            <EmptyState
              icon={History}
              title={t(msg`No activity yet`)}
              description={t(msg`Changes in your academy will appear here.`)}
            />
          }
        >
          <ul class="space-y-3" data-testid="activity-feed">
            <For each={groups()}>
              {(group) => (
                <li
                  class="rounded-2xl border border-border bg-surface p-4"
                  data-testid="activity-group"
                >
                  <For each={group.entries}>
                    {(row) => (
                      <div
                        class="flex items-start gap-3 py-1.5 first:pt-0 last:pb-0"
                        data-testid="activity-entry"
                        data-action={row.action}
                        data-entity={row.entityType}
                      >
                        <div class="min-w-0 flex-1 text-sm">
                          <p class="leading-snug">
                            <span class="font-bold">
                              {row.actorUserId === userId()
                                ? t(msg`You`)
                                : (row.actorName ?? t(msg`Someone`))}
                            </span>{" "}
                            <span>{t(actionText(row.action))}</span>{" "}
                            <span class="text-muted-foreground">
                              {t(entityTypeText(row.entityType))}
                            </span>
                            <Show when={entityLabel(row)}>
                              {(label) => (
                                <Show
                                  when={entityLink(row)}
                                  fallback={<span class="font-medium"> “{label()}”</span>}
                                >
                                  {(href) => (
                                    <Link
                                      to={href()}
                                      class="font-medium text-primary hover:underline"
                                    >
                                      {" "}
                                      “{label()}”
                                    </Link>
                                  )}
                                </Show>
                              )}
                            </Show>
                          </p>
                          <p class="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{fmtTs(row.serverTs)}</span>
                            <Show when={isOfflineEdit(row)}>
                              <span
                                class="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5"
                                data-testid="activity-offline-badge"
                              >
                                <WifiOff class="h-3 w-3" />
                                {t(msg`Offline`)}
                              </span>
                            </Show>
                          </p>
                        </div>
                        {/*
                          `revertible` is decided by the SERVER (`is_revertible`) and
                          travels in the DTO. The UI doesn't reimplement the
                          reversible-actions matrix: that would be the same rule in two
                          places (DATA_ACCESS_POLICY §3.2).
                        */}
                        <Show when={row.revertible}>
                          <button
                            type="button"
                            data-testid="activity-revert"
                            onClick={() => void revert(row)}
                            class="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                          >
                            <RotateCcw class="h-3.5 w-3.5" />
                            {t(msg`Revert`)}
                          </button>
                        </Show>
                      </div>
                    )}
                  </For>
                </li>
              )}
            </For>
          </ul>

          <Show when={cursor() !== null && cursor() !== undefined}>
            <div class="flex justify-center">
              <button
                type="button"
                data-testid="activity-load-older"
                disabled={loadingOlder()}
                onClick={() => void loadOlder()}
                class="rounded-xl border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                {loadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
              </button>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
};
